import React, { useState, useMemo, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiCreditCard } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { STRIPE_CONFIG } from '../../config/stripe';
import { getPaymentBuckets } from '../../config/paymentMethodLabels';
import { deltaReturnKey } from './deltaPayment';
import PaymentMethodSelector from '../ui/PaymentMethodSelector';
import PayPalDeltaButton from './PayPalDeltaButton';

const API_BASE = STRIPE_CONFIG.apiBaseUrl || '/api';

let stripePromise = null;
const getStripePromise = () => {
  if (!stripePromise) stripePromise = loadStripe(STRIPE_CONFIG.publishableKey);
  return stripePromise;
};

// Inner Stripe form. Card confirms inline; redirect methods (MobilePay,
// Pay-by-Bank) navigate away here and are finalized by useDeltaPaymentReturn.
function StripeForm({ amount, onPaid, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.origin + window.location.pathname },
        redirect: 'if_required'
      });
      if (error) { onError(error.message); return; }
      if (paymentIntent?.status === 'succeeded') {
        await onPaid(paymentIntent.id, { skipConfirm: false });
      } else {
        onError(t('manage.paymentNotCompleted', 'Payment was not completed. Please try again.'));
      }
    } catch (err) {
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full py-4 rounded-xl font-semibold text-white transition-colors disabled:opacity-50"
        style={{ backgroundColor: theme.colors.primary.base }}
      >
        {processing
          ? t('manage.processing', 'Processing...')
          : `${t('manage.payNow', 'Pay Now')} ${formatCurrency(amount)}`}
      </button>
    </form>
  );
}

/**
 * Shared payment-method UI for the manage delta flows (pay / extend / edit).
 *
 * Renders the bucket model (card-or-bank, MobilePay isolated, PayPal direct)
 * plus the optional saved-card one-tap. Creates a bucket-scoped PaymentIntent,
 * stashes `applyContext` (keyed by PI id) before any redirect, and calls
 * `onPaid(paymentIntentId, { skipConfirm })` once the payment is taken inline.
 *
 * The parent supplies `onPaid` (typically wrapping a shared onFinalize that
 * confirms server-side + applies the extension/edit/addons), so this component
 * stays UI-only and identical across flows.
 */
export default function DeltaPaymentMethods({
  token,
  merchant,
  booking,
  amount,
  description,
  applyContext,
  savedCard = null,
  idempotencyPrefix = 'delta',
  onPaid,
  onError,
  // When true, the PayPal capture endpoint fires the Make.com notification
  // itself. Set by the balance-payment flow only when no add-ons follow; the
  // extend/edit flows leave it false because their own apply endpoints notify.
  paypalNotifyOnCapture = false,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { formatCurrency } = useCurrency();

  // Match the Stripe Element UI language to the app language (defaults to
  // 'auto' / browser locale otherwise). Stripe supports en/de/es/fr/it/hu/fi.
  const stripeLocale = i18n.language || 'en';

  // Mirror the backend needsOffSession: a first payment that saves a method
  // off-session can't use MobilePay / Pay-by-Bank.
  const needsOffSession = (parseFloat(booking.amount_paid) || 0) === 0
    && (merchant?.deposits_enabled || merchant?.off_session_payments);

  const buckets = useMemo(() => getPaymentBuckets({
    paymentMethodTypes: merchant?.payment_method_types || [],
    currency: booking.currency,
    needsOffSession,
    paypalDirectEnabled: !!merchant?.paypal_direct_enabled,
  }), [merchant?.payment_method_types, merchant?.paypal_direct_enabled, booking.currency, needsOffSession]);

  const [useSaved, setUseSaved] = useState(!!savedCard);
  const [selectedBucketId, setSelectedBucketId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [busy, setBusy] = useState(false);

  const activeBucket = buckets.find(b => b.id === selectedBucketId) || null;

  const formatCardBrand = (brand) => (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : '');

  const persistContext = (piId) => {
    try {
      sessionStorage.setItem(deltaReturnKey(piId), JSON.stringify({ applyContext }));
    } catch (e) { /* sessionStorage unavailable — card (no redirect) still works */ }
  };

  // Create a bucket-scoped PaymentIntent and reveal its Payment Element. Recreate
  // per bucket so the method list / surcharge always matches the active bucket.
  const selectBucket = async (bucketId) => {
    onError(null);
    const bucket = buckets.find(b => b.id === bucketId);
    if (!bucket) return;
    setSelectedBucketId(bucketId);
    setClientSecret(null);
    if (bucket.kind === 'paypal') return; // PayPalDeltaButton renders below

    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/manage/create-delta-payment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          description,
          paymentMethodTypes: bucket.methodTypes,
          allowCardFallback: bucket.allowCardFallback
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create payment');
      }
      const data = await res.json();
      persistContext(data.paymentIntentId);
      setClientSecret(data.clientSecret);
    } catch (err) {
      onError(err.message);
      setSelectedBucketId(null);
    } finally {
      setBusy(false);
    }
  };

  // Auto-select when there is exactly one bucket and no saved-card shortcut.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (useSaved) return;
    if (buckets.length === 1 && !selectedBucketId) {
      autoSelectedRef.current = true;
      selectBucket(buckets[0].id);
    }
  }, [useSaved, buckets.length, selectedBucketId]);

  const paySavedCard = async () => {
    onError(null);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/manage/create-delta-payment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          description,
          useSavedPaymentMethod: true,
          idempotencyKey: `${idempotencyPrefix}-${booking.id}-${Date.now()}`
        })
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (data.code === 'SAVED_PM_DECLINED' || data.code === 'SAVED_PM_UNAVAILABLE') {
          setUseSaved(false);
          onError(t('manage.savedCardDeclined', "That card couldn't be charged. Please use a different card."));
          return;
        }
        throw new Error(data.error || 'Failed to create payment');
      }

      if (data.status === 'succeeded') {
        await onPaid(data.paymentIntentId, { skipConfirm: false });
        return;
      }
      if (data.status === 'requires_action') {
        const stripe = await getStripePromise();
        const { error: actionError, paymentIntent } = await stripe.handleNextAction({ clientSecret: data.clientSecret });
        if (actionError) { onError(actionError.message); return; }
        if (paymentIntent?.status === 'succeeded') {
          await onPaid(paymentIntent.id, { skipConfirm: false });
          return;
        }
      }
      onError(t('manage.paymentNotCompleted', 'Payment was not completed. Please try again.'));
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const resetSelection = () => {
    setSelectedBucketId(null);
    setClientSecret(null);
    onError(null);
  };

  // --- Saved-card one-tap ---
  if (useSaved && savedCard) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <SafeIcon icon={FiCreditCard} className="w-5 h-5 text-gray-500" />
          <span className="text-sm text-gray-900">
            {t('manage.savedCardLine', '{{brand}} ending in {{last4}}', { brand: formatCardBrand(savedCard.brand), last4: savedCard.last4 })}
          </span>
        </div>
        <button
          onClick={paySavedCard}
          disabled={busy}
          className="w-full py-4 rounded-xl font-semibold text-white transition-colors disabled:opacity-50"
          style={{ backgroundColor: theme.colors.primary.base }}
        >
          {`${t('manage.payNow', 'Pay')} ${formatCurrency(amount)}`}
        </button>
        <button
          onClick={() => { setUseSaved(false); onError(null); }}
          className="w-full py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        >
          {t('manage.useDifferentMethod', 'Use a different payment method')}
        </button>
      </div>
    );
  }

  // --- Active Stripe bucket: Payment Element ---
  if (activeBucket?.kind === 'stripe' && clientSecret) {
    return (
      <div className="space-y-4">
        {(buckets.length > 1 || savedCard) && (
          <button
            onClick={savedCard ? () => setUseSaved(true) : resetSelection}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            {t('manage.changePaymentMethod', 'Change payment method')}
          </button>
        )}
        {/* key forces a full remount per bucket so the method list / surcharge refresh */}
        <Elements key={activeBucket.id} stripe={getStripePromise()} options={{ clientSecret, appearance: { theme: 'stripe' }, locale: stripeLocale }}>
          <StripeForm amount={amount} onPaid={onPaid} onError={onError} />
        </Elements>
      </div>
    );
  }

  // --- Active PayPal bucket ---
  if (activeBucket?.kind === 'paypal') {
    return (
      <div className="space-y-4">
        <button
          onClick={savedCard ? () => setUseSaved(true) : resetSelection}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          {t('manage.changePaymentMethod', 'Change payment method')}
        </button>
        <PayPalDeltaButton
          token={token}
          amount={amount}
          description={description}
          onSuccess={(captureId) => onPaid(captureId, { skipConfirm: true })}
          onError={(msg) => onError(msg)}
          triggerNotificationOnCapture={paypalNotifyOnCapture}
        />
      </div>
    );
  }

  // --- Bucket selection ---
  return (
    <div className="space-y-3">
      <PaymentMethodSelector
        buckets={buckets}
        selectedId={activeBucket?.id}
        onSelect={selectBucket}
        disabled={busy}
      />
      {savedCard && (
        <button
          onClick={() => setUseSaved(true)}
          className="w-full py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        >
          {t('manage.useSavedCard', 'Use saved card')}
        </button>
      )}
      {busy && (
        <div role="status" className="flex justify-center py-4">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
          <span className="sr-only">{t('manage.processing', 'Processing...')}</span>
        </div>
      )}
    </div>
  );
}
